import { motion } from "framer-motion";
import { Helmet } from "react-helmet-async";
import {
  Flame, BadgePercent, Coffee, Truck, GraduationCap, Sparkles,
  UtensilsCrossed, Hotel, Building2, Store, Check, Download,
  ChevronDown, Instagram, Youtube, Mail, MapPin, Clock, ArrowLeft, Phone, Smartphone,
} from "lucide-react";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "@/components/ui/accordion";
import { Header } from "@/components/joffreys/Header";
import { FloatingWA } from "@/components/joffreys/FloatingWA";
import { CountUp } from "@/components/joffreys/CountUp";
import { WhatsAppIcon } from "@/components/joffreys/WhatsAppIcon";
import { getWaLink, WA_MESSAGES } from "@/lib/joffreys-whatsapp";

import "@/styles/joffreys.css";

import heroBeans from "@/assets/joffreys/hero-beans.jpg";
import catEspresso from "@/assets/joffreys/cat-espresso.jpg";
import catFilter from "@/assets/joffreys/cat-filter.jpg";
import catFrench from "@/assets/joffreys/cat-french.jpg";
import catTurkish from "@/assets/joffreys/cat-turkish.jpg";

const fadeUp = {
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-80px" },
  transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] as [number, number, number, number] },
};

function SectionEyebrow({ children }: { children: React.ReactNode }) {
  return (
    <div className="inline-flex items-center gap-2 rounded-full border border-primary/20 bg-primary-50 px-4 py-1.5 text-xs font-bold uppercase tracking-widest text-primary">
      {children}
    </div>
  );
}

const orgJsonLd = {
  "@context": "https://schema.org",
  "@type": "LocalBusiness",
  name: "محمصة جوفريز - Joffrey's Roastery",
  description: "محمصة سعودية للقهوة المختصة. باقات جملة لمقاهي ومطاعم وفنادق المملكة.",
  url: "https://joffreysme.com",
  telephone: ["+966593777448", "+966126526665"],
  email: "Info@joffreysme.com",
  vatID: "300588668200003",
  address: { "@type": "PostalAddress", addressCountry: "SA" },
  areaServed: "Saudi Arabia",
};

export default function Joffreysme() {
  return (
    <div className="joffreys-root min-h-screen overflow-x-hidden" dir="rtl" lang="ar">
      <Helmet>
        <title>جوفريز للجملة — قهوة مختصة محمصة طازجة لمقهاك | Joffrey&apos;s Wholesale</title>
        <meta
          name="description"
          content="محمصة جوفريز السعودية. باقات جملة لمقاهي ومطاعم وفنادق المملكة. تحميص يومي، شحن لكل السعودية، أسعار تبدأ من باقة 10 كجم. اطلب الكتالوج عبر واتساب."
        />
        <meta name="theme-color" content="#67119c" />
        <meta property="og:title" content="جوفريز للجملة — قهوة مختصة" />
        <meta property="og:description" content="باقات جملة لمقاهي ومطاعم وفنادق المملكة. اطلب الكتالوج عبر واتساب." />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
        <script type="application/ld+json">{JSON.stringify(orgJsonLd)}</script>
      </Helmet>

      <Header />
      <main>
        <Hero />
        <StatsBar />
        <WhySection />
        <CategoriesSection />
        <PackagesSection />
        <ServeSection />
        <HowItWorks />
        <SocialProof />
        <FAQSection />
        <FinalCTA />
      </main>
      <Footer />
      <FloatingWA />
    </div>
  );
}

/* ───────────────────── HERO ───────────────────── */
function Hero() {
  return (
    <section id="hero" className="relative min-h-[92vh] flex items-center justify-center text-white overflow-hidden">
      <img
        src={heroBeans}
        alt="حبوب قهوة محمصة طازجة"
        className="absolute inset-0 h-full w-full object-cover"
        width={1920}
        height={1080}
      />
      <div className="absolute inset-0 bg-hero-overlay" />
      <div className="absolute inset-0 bg-radial-glow" />

      <div className="relative z-10 max-w-4xl mx-auto px-6 lg:px-8 text-center pt-24 pb-16">
        <motion.div {...fadeUp}>
          <span className="inline-flex items-center rounded-full border border-gold/60 bg-gold/10 px-4 py-1.5 text-xs font-bold tracking-wide text-gold">
            للمقاهي · المطاعم · الفنادق · المكاتب
          </span>
        </motion.div>

        <motion.h1
          {...fadeUp}
          transition={{ ...fadeUp.transition, delay: 0.08 }}
          className="mt-6 font-black tracking-tight text-balance leading-[1.1]"
          style={{ fontSize: "clamp(2.5rem, 6vw, 5rem)" }}
        >
          قهوة مختصة...{" "}
          <span className="bg-gradient-to-l from-gold to-gold-dark bg-clip-text text-transparent">
            بأسعار الجملة
          </span>
        </motion.h1>

        <motion.p
          {...fadeUp}
          transition={{ ...fadeUp.transition, delay: 0.16 }}
          className="mt-6 text-xl text-cream/90 max-w-2xl mx-auto"
        >
          محمصة سعودية تحمّص حبوبها بنفسها يومياً. شراكات جملة لمقهاك أو مطعمك بجودة استثنائية، شحن لكل المملكة، وأسعار لا تُقاوم.
        </motion.p>

        <motion.div
          {...fadeUp}
          transition={{ ...fadeUp.transition, delay: 0.24 }}
          className="mt-8 flex flex-wrap justify-center items-center gap-x-3 gap-y-2 text-sm text-cream/80"
        >
          <span>تحميص يومي</span><span className="text-gold">·</span>
          <span>شحن لكل السعودية</span><span className="text-gold">·</span>
          <span>أسعار حصرية للجملة</span><span className="text-gold">·</span>
          <span>100٪ بن مختص</span>
        </motion.div>

        <motion.div
          {...fadeUp}
          transition={{ ...fadeUp.transition, delay: 0.32 }}
          className="mt-10 flex flex-col sm:flex-row gap-4 justify-center items-center"
        >
          <a
            href={getWaLink(WA_MESSAGES.hero)}
            target="_blank" rel="noopener noreferrer"
            className="group inline-flex items-center gap-3 rounded-full bg-gradient-purple px-8 py-4 text-base font-bold shadow-2xl shadow-purple transition-all hover:scale-[1.03]"
          >
            <WhatsAppIcon className="h-5 w-5" />
            اطلب عرض الجملة عبر واتساب
          </a>
          <a
            href={getWaLink(WA_MESSAGES.catalog)}
            target="_blank" rel="noopener noreferrer"
            className="inline-flex items-center gap-2 rounded-full border-2 border-cream/40 bg-transparent px-7 py-3.5 text-base font-semibold text-cream hover:bg-cream/10 transition"
          >
            <Download className="h-5 w-5" />
            حمّل كتالوج الأسعار
          </a>
        </motion.div>

        <motion.p
          {...fadeUp}
          transition={{ ...fadeUp.transition, delay: 0.4 }}
          className="mt-5 text-sm text-cream/70"
        >
          رد خلال 24 ساعة · بدون التزام
        </motion.p>
      </div>

      <a href="#stats" className="absolute bottom-6 left-1/2 -translate-x-1/2 text-cream/70 flex flex-col items-center gap-1 text-xs">
        <span>اكتشف</span>
        <ChevronDown className="h-5 w-5 animate-bounce" />
      </a>
    </section>
  );
}

/* ───────────────────── STATS BAR ───────────────────── */
function StatsBar() {
  const stats = [
    { num: 50, prefix: "+", label: "شريك تجاري نشط" },
    { num: 10, prefix: "+", suffix: " طن", label: "قهوة سنوياً" },
    { num: 24, suffix: " ساعة", label: "تحميص قبل الشحن" },
    { num: 100, suffix: "٪", label: "بن مختص" },
  ];
  return (
    <section id="stats" className="relative bg-coffee text-cream py-16 bean-pattern">
      <div className="max-w-7xl mx-auto px-6 lg:px-8 grid grid-cols-2 md:grid-cols-4 gap-10 text-center">
        {stats.map((s) => (
          <div key={s.label}>
            <div className="text-5xl md:text-6xl font-black text-gold tracking-tight">
              <CountUp to={s.num} prefix={s.prefix ?? ""} suffix={s.suffix ?? ""} />
            </div>
            <div className="mt-2 text-xs md:text-sm uppercase tracking-widest text-cream/80 font-medium">
              {s.label}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

/* ───────────────────── WHY ───────────────────── */
function WhySection() {
  const items = [
    { icon: Flame, t: "تحميص طازج يومي", d: "نحمّص طلبك خلال 24 ساعة قبل الشحن — قهوة في ذروة نضجها" },
    { icon: BadgePercent, t: "أسعار جملة تنافسية", d: "كلما زاد طلبك، انخفض السعر — خصومات تصل لـ 35٪" },
    { icon: Coffee, t: "تنوع لا محدود", d: "+12 نوع بن من إثيوبيا، كولومبيا، اليمن، البرازيل، الهندوراس" },
    { icon: Truck, t: "شحن سريع وموثوق", d: "تسليم لكل مناطق المملكة خلال 2-5 أيام عمل" },
    { icon: GraduationCap, t: "تدريب باريستا مجاني", d: "نرسل خبيرنا لتدريب فريقك على استخراج أفضل نكهة من حبوبنا" },
    { icon: Sparkles, t: "خلطات مخصصة", d: "نصمّم لك خلطة Signature بهوية مقهاك ونكهة حصرية لعملائك" },
  ];
  return (
    <section id="why" className="py-24 lg:py-32">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="max-w-3xl">
          <motion.div {...fadeUp}><SectionEyebrow>لماذا جوفريز</SectionEyebrow></motion.div>
          <motion.h2 {...fadeUp} transition={{ ...fadeUp.transition, delay: 0.08 }}
            className="mt-5 font-black tracking-tight text-balance"
            style={{ fontSize: "clamp(2rem, 4vw, 3.25rem)" }}>
            ست أسباب تجعل جوفريز شريكك المثالي للجملة
          </motion.h2>
        </div>

        <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {items.map((it, i) => (
            <motion.div
              key={it.t}
              {...fadeUp}
              transition={{ ...fadeUp.transition, delay: 0.05 * i }}
              whileHover={{ y: -6 }}
              className="group rounded-2xl bg-card border border-primary-50 p-8 transition-all hover:border-primary hover:shadow-purple"
            >
              <div className="mb-5 inline-flex h-14 w-14 items-center justify-center rounded-2xl bg-primary-50 text-primary group-hover:bg-primary group-hover:text-primary-foreground transition-colors">
                <it.icon className="h-7 w-7" strokeWidth={1.75} />
              </div>
              <h3 className="text-xl font-bold text-coffee">{it.t}</h3>
              <p className="mt-3 text-muted-foreground leading-relaxed">{it.d}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ───────────────────── CATEGORIES ───────────────────── */
function CategoriesSection() {
  const cats = [
    { name: "إسبريسو", desc: "للمقاهي والكافيهات. خلطات بكثافة كريمية مثالية.", img: catEspresso },
    { name: "فلتر", desc: "للقهوة المختصة المقطّرة. حموضة متوازنة ووضوح في النكهة.", img: catFilter },
    { name: "فرنسي بريس", desc: "لطعم غني وكامل، مع نكهات الشوكولاتة والمكسرات.", img: catFrench },
    { name: "تركي", desc: "قهوة عربية وتركية أصيلة، طحن ناعم وقوام كثيف.", img: catTurkish },
  ];
  return (
    <section className="py-24 lg:py-32 bg-primary-50/40">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="max-w-3xl">
          <motion.div {...fadeUp}><SectionEyebrow>حبوبنا</SectionEyebrow></motion.div>
          <motion.h2 {...fadeUp} transition={{ ...fadeUp.transition, delay: 0.08 }}
            className="mt-5 font-black tracking-tight text-balance"
            style={{ fontSize: "clamp(2rem, 4vw, 3.25rem)" }}>
            حبوب مختارة لكل ماكينة... ولكل ذوق
          </motion.h2>
        </div>

        <div className="mt-14 grid sm:grid-cols-2 gap-6">
          {cats.map((c, i) => (
            <motion.div
              key={c.name}
              {...fadeUp}
              transition={{ ...fadeUp.transition, delay: 0.06 * i }}
              className="group relative overflow-hidden rounded-3xl aspect-[4/5] bg-coffee"
            >
              <img
                src={c.img}
                alt={c.name}
                loading="lazy"
                className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-110"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-charcoal via-charcoal/40 to-transparent" />
              <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity bg-primary/30 mix-blend-multiply" />
              <div className="absolute inset-x-0 bottom-0 p-8 text-white">
                <h3 className="text-3xl font-black">{c.name}</h3>
                <p className="mt-2 text-cream/85 max-w-sm">{c.desc}</p>
                <a
                  href={getWaLink(WA_MESSAGES.sample(c.name))}
                  target="_blank" rel="noopener noreferrer"
                  className="mt-5 inline-flex items-center gap-2 text-gold font-bold hover:gap-3 transition-all"
                >
                  اطلب عينة
                  <ArrowLeft className="h-4 w-4" />
                </a>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ───────────────────── PACKAGES ───────────────────── */
function PackagesSection() {
  const pkgs = [
    {
      name: "باقة الانطلاق", tagline: "للمقاهي الناشئة والمحلات الصغيرة",
      qty: "10 – 25 كجم",
      features: [
        "خصم 15٪ على الأسعار الفردية",
        "شحن مجاني للطلبات +20 كجم",
        "استشارة باريستا أونلاين",
        "4 عينات مجانية للبدء",
      ],
      cta: "ابدأ معنا واتساب",
      msg: WA_MESSAGES.starter,
      highlight: false,
    },
    {
      name: "باقة المحترف", tagline: "للمقاهي المتنامية والمطاعم",
      qty: "25 – 100 كجم",
      features: [
        "خصم 25٪ ثابت",
        "شحن مجاني دائماً",
        "زيارة باريستا للمقهى مرة شهرياً",
        "خلطة باسم مقهاك",
        "أولوية في التحميص والتسليم",
      ],
      cta: "احجز الباقة واتساب",
      msg: WA_MESSAGES.pro,
      highlight: true,
    },
    {
      name: "باقة السلسلة", tagline: "للسلاسل والفنادق والمنتجعات",
      qty: "+100 كجم",
      features: [
        "خصم يصل لـ 35٪",
        "شحن أولوية + جدول تسليم ثابت",
        "باريستا مخصص + تدريب فريق كامل",
        "خلطة Signature + تغليف بهويتك",
        "حساب مدير علاقات شخصي",
      ],
      cta: "كلّمنا الآن واتساب",
      msg: WA_MESSAGES.chain,
      highlight: false,
    },
  ];

  return (
    <section id="packages" className="py-24 lg:py-32">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="max-w-3xl text-center mx-auto">
          <motion.div {...fadeUp} className="flex justify-center"><SectionEyebrow>باقات الجملة</SectionEyebrow></motion.div>
          <motion.h2 {...fadeUp} transition={{ ...fadeUp.transition, delay: 0.08 }}
            className="mt-5 font-black tracking-tight text-balance"
            style={{ fontSize: "clamp(2rem, 4vw, 3.25rem)" }}>
            اختر باقة جملتك
          </motion.h2>
          <motion.p {...fadeUp} transition={{ ...fadeUp.transition, delay: 0.14 }}
            className="mt-4 text-lg text-muted-foreground">
            كل الباقات تشمل تحميص يومي + ضمان جودة + استشارة مجانية
          </motion.p>
        </div>

        <div className="mt-16 grid lg:grid-cols-3 gap-6 lg:gap-8 items-stretch">
          {pkgs.map((p, i) => (
            <motion.div
              key={p.name}
              {...fadeUp}
              transition={{ ...fadeUp.transition, delay: 0.08 * i }}
              className={`relative rounded-3xl p-8 lg:p-10 border-2 flex flex-col ${
                p.highlight
                  ? "bg-card ring-2 ring-primary border-primary lg:scale-105 shadow-purple z-10"
                  : "bg-card border-border"
              }`}
            >
              {p.highlight && (
                <div className="absolute -top-4 left-1/2 -translate-x-1/2 rounded-full bg-gradient-gold px-4 py-1.5 text-xs font-black uppercase tracking-widest text-coffee shadow-lg">
                  الأكثر طلباً
                </div>
              )}

              {p.highlight && (
                <div className="-mx-8 lg:-mx-10 -mt-8 lg:-mt-10 mb-6 px-8 lg:px-10 py-6 rounded-t-3xl bg-gradient-gold/10">
                  <h3 className="text-2xl font-black text-primary">{p.name}</h3>
                  <p className="mt-1 text-sm text-coffee/70">{p.tagline}</p>
                </div>
              )}
              {!p.highlight && (
                <>
                  <h3 className="text-2xl font-black text-coffee">{p.name}</h3>
                  <p className="mt-1 text-sm text-muted-foreground">{p.tagline}</p>
                </>
              )}

              <div className="mt-6 pb-6 border-b border-border">
                <div className="text-xs uppercase tracking-widest text-muted-foreground">الكمية الشهرية</div>
                <div className="mt-1 text-3xl font-black text-primary">{p.qty}</div>
              </div>

              <ul className="mt-6 space-y-3 flex-1">
                {p.features.map((f) => (
                  <li key={f} className="flex items-start gap-3">
                    <span className="mt-0.5 inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary-50 text-primary">
                      <Check className="h-3.5 w-3.5" strokeWidth={3} />
                    </span>
                    <span className="text-foreground/90">{f}</span>
                  </li>
                ))}
              </ul>

              <a
                href={getWaLink(p.msg)}
                target="_blank" rel="noopener noreferrer"
                className={`mt-8 inline-flex items-center justify-center gap-2 rounded-full px-6 py-3.5 font-bold transition-all hover:scale-[1.02] ${
                  p.highlight
                    ? "bg-gradient-purple text-primary-foreground shadow-purple"
                    : "bg-coffee text-cream hover:bg-charcoal"
                }`}
              >
                <WhatsAppIcon className="h-5 w-5" />
                {p.cta}
              </a>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ───────────────────── SERVE ───────────────────── */
function ServeSection() {
  const items = [
    { icon: Coffee, label: "المقاهي والكافيهات" },
    { icon: UtensilsCrossed, label: "المطاعم" },
    { icon: Hotel, label: "الفنادق والمنتجعات" },
    { icon: Building2, label: "الشركات والمكاتب" },
    { icon: Store, label: "محلات التجزئة" },
    { icon: GraduationCap, label: "الجامعات والأكاديميات" },
  ];
  return (
    <section id="serve" className="py-24 lg:py-32 bg-primary-50/40">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="text-center max-w-3xl mx-auto">
          <motion.h2 {...fadeUp} className="font-black tracking-tight text-balance"
            style={{ fontSize: "clamp(2rem, 4vw, 3.25rem)" }}>
            من نخدم
          </motion.h2>
        </div>

        <div className="mt-14 flex md:grid md:grid-cols-3 lg:grid-cols-6 gap-4 overflow-x-auto pb-4 md:overflow-visible snap-x snap-mandatory">
          {items.map((it, i) => (
            <motion.div
              key={it.label}
              {...fadeUp}
              transition={{ ...fadeUp.transition, delay: 0.04 * i }}
              className="snap-center shrink-0 w-44 md:w-auto group rounded-2xl border border-border bg-card p-6 text-center transition-all hover:bg-primary hover:border-primary hover:-translate-y-1 cursor-default"
            >
              <div className="mx-auto mb-3 inline-flex h-12 w-12 items-center justify-center rounded-xl bg-primary-50 text-primary group-hover:bg-white/20 group-hover:text-white transition-colors">
                <it.icon className="h-6 w-6" strokeWidth={1.75} />
              </div>
              <div className="text-sm font-bold text-coffee group-hover:text-white transition-colors">{it.label}</div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ───────────────────── HOW IT WORKS ───────────────────── */
function HowItWorks() {
  const steps = [
    { t: "تواصل واتساب", d: "كلّمنا واطلب كتالوج الجملة الكامل" },
    { t: "استلم العينات", d: "نرسل لك 4 عينات مجانية من خلطاتنا الأكثر طلباً" },
    { t: "اختر باقتك", d: "حدد الكمية، الباقة، وجدول التسليم" },
    { t: "استلم قهوتك", d: "تحميص + شحن خلال أيام، وتبدأ تجربة جوفريز" },
  ];
  return (
    <section className="py-24 lg:py-32">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="max-w-3xl">
          <motion.div {...fadeUp}><SectionEyebrow>الخطوات</SectionEyebrow></motion.div>
          <motion.h2 {...fadeUp} transition={{ ...fadeUp.transition, delay: 0.08 }}
            className="mt-5 font-black tracking-tight text-balance"
            style={{ fontSize: "clamp(2rem, 4vw, 3.25rem)" }}>
            كيف نبدأ شراكتنا؟
          </motion.h2>
        </div>

        <div className="relative mt-14 grid md:grid-cols-4 gap-10 md:gap-6">
          <div aria-hidden className="hidden md:block absolute top-8 right-[12%] left-[12%] border-t-2 border-dashed border-primary/30" />
          {steps.map((s, i) => (
            <motion.div key={s.t} {...fadeUp} transition={{ ...fadeUp.transition, delay: 0.08 * i }}
              className="relative text-center md:text-right">
              <div className="mx-auto md:mx-0 relative z-10 h-16 w-16 rounded-full bg-gradient-purple text-primary-foreground flex items-center justify-center text-2xl font-black shadow-purple">
                {i + 1}
              </div>
              <h3 className="mt-5 text-xl font-black text-coffee">{s.t}</h3>
              <p className="mt-2 text-muted-foreground leading-relaxed">{s.d}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ───────────────────── SOCIAL PROOF ───────────────────── */
function SocialProof() {
  const partners = [
    "Brew Lab", "Salt & Bean", "Olea", "Cafe Nova",
    "Roastery 21", "Vert Coffee", "Mocha House", "Aurora",
  ];
  const testimonials = [
    { q: "جوفريز غيّروا قهوتنا من جيدة إلى استثنائية. التحميص الطازج يومياً يفرق فعلاً مع عملائنا.", n: "أحمد العتيبي", b: "مدير مقهى Brew Lab", c: "الرياض" },
    { q: "أسعار الجملة عادلة، الجودة ثابتة، والدعم محترف. شراكتنا معهم سنة كاملة بدون مشاكل.", n: "نورة السبيعي", b: "مالكة Salt & Bean", c: "جدة" },
    { q: "خلطة Signature اللي صمّموها لمطعمنا صارت من أهم أسباب رجوع العملاء.", n: "فيصل القحطاني", b: "مدير عمليات سلسلة Olea", c: "الدمام" },
  ];
  return (
    <section className="py-24 lg:py-32 bg-primary-50/40">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <motion.h2 {...fadeUp} className="text-center font-black tracking-tight"
          style={{ fontSize: "clamp(2rem, 4vw, 3.25rem)" }}>
          يثقون بنا
        </motion.h2>

        <div className="mt-12 grid grid-cols-2 md:grid-cols-4 gap-6">
          {partners.map((p) => (
            <div key={p} className="flex items-center justify-center h-20 rounded-2xl bg-card border border-border grayscale opacity-60 hover:grayscale-0 hover:opacity-100 transition">
              <span className="text-lg font-black text-coffee/80">{p}</span>
            </div>
          ))}
        </div>

        <div className="mt-16 grid md:grid-cols-3 gap-6">
          {testimonials.map((t, i) => (
            <motion.figure
              key={t.n}
              {...fadeUp}
              transition={{ ...fadeUp.transition, delay: 0.06 * i }}
              className="rounded-3xl bg-card border border-border p-8 hover:shadow-purple transition-shadow"
            >
              <div className="text-5xl text-primary/20 leading-none font-black">”</div>
              <blockquote className="mt-2 text-lg text-coffee leading-relaxed">{t.q}</blockquote>
              <figcaption className="mt-6 flex items-center gap-3 pt-6 border-t border-border">
                <div className="h-12 w-12 rounded-full bg-gradient-purple flex items-center justify-center text-white font-black">
                  {t.n.charAt(0)}
                </div>
                <div>
                  <div className="font-bold text-coffee">{t.n}</div>
                  <div className="text-xs text-muted-foreground">{t.b} · {t.c}</div>
                </div>
              </figcaption>
            </motion.figure>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ───────────────────── FAQ ───────────────────── */
function FAQSection() {
  const faqs = [
    { q: "ما الحد الأدنى لطلب الجملة؟", a: "الحد الأدنى 10 كجم شهرياً للبدء بباقة الانطلاق." },
    { q: "كيف تضمنون طزاجة القهوة؟", a: "نحمّص طلبك خلال 24 ساعة قبل الشحن، ويصلك خلال 2-5 أيام بأقصى تقدير، أي تستلم القهوة في أفضل نوافذها (5-21 يوم بعد التحميص)." },
    { q: "هل يمكنكم تصميم خلطة خاصة لمقهاي؟", a: "نعم، الخلطات المخصصة جزء من باقتي المحترف والسلسلة. نعمل معك على ملف نكهة خاص بهوية مقهاك." },
    { q: "ما تكاليف الشحن؟", a: "الشحن مجاني لكل طلبات +20 كجم، ومجاني دائماً لباقتي المحترف والسلسلة. للطلبات الأقل، رسوم رمزية حسب المنطقة." },
    { q: "هل توفرون كتالوج بالأسعار؟", a: "نعم، نرسل الكتالوج كامل عبر واتساب فور تواصلك معنا." },
    { q: "هل تقدمون تدريب للباريستا؟", a: "نعم. أونلاين لباقة الانطلاق، حضوري شهري لباقة المحترف، وتدريب فريق كامل لباقة السلسلة." },
    { q: "ما طرق الدفع المتاحة؟", a: "تحويل بنكي، مدى أعمال، فيزا، وآجل (Net 30) للعملاء بعد 3 أشهر شراكة." },
    { q: "كم تستغرق فترة التسليم بعد الطلب؟", a: "تحميص خلال 24 ساعة + شحن 2-5 أيام = إجمالاً 3-6 أيام عمل." },
  ];
  return (
    <section id="faq" className="py-24 lg:py-32">
      <div className="max-w-3xl mx-auto px-6 lg:px-8">
        <motion.h2 {...fadeUp} className="text-center font-black tracking-tight text-balance"
          style={{ fontSize: "clamp(2rem, 4vw, 3.25rem)" }}>
          أسئلة يسألها كل شريك جديد
        </motion.h2>

        <Accordion type="single" collapsible className="mt-12 space-y-3">
          {faqs.map((f, i) => (
            <AccordionItem key={i} value={`item-${i}`} className="rounded-2xl border border-border bg-card px-6 data-[state=open]:border-primary transition">
              <AccordionTrigger className="text-right text-base md:text-lg font-bold text-coffee hover:no-underline py-5">
                {f.q}
              </AccordionTrigger>
              <AccordionContent className="text-muted-foreground text-base leading-relaxed pb-5">
                {f.a}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </div>
    </section>
  );
}

/* ───────────────────── FINAL CTA ───────────────────── */
function FinalCTA() {
  return (
    <section id="contact" className="relative bg-primary text-white overflow-hidden">
      <div className="absolute inset-0 bean-pattern opacity-50" />
      <div className="absolute inset-0 bg-radial-glow opacity-60" />
      <div className="relative max-w-3xl mx-auto px-6 lg:px-8 py-32 text-center">
        <motion.div {...fadeUp}>
          <span className="inline-flex items-center rounded-full border border-gold/60 bg-gold/10 px-4 py-1.5 text-xs font-bold tracking-widest text-gold uppercase">
            جاهز تبدأ؟
          </span>
        </motion.div>
        <motion.h2 {...fadeUp} transition={{ ...fadeUp.transition, delay: 0.08 }}
          className="mt-6 font-black tracking-tight text-balance leading-[1.1]"
          style={{ fontSize: "clamp(2.25rem, 5vw, 4rem)" }}>
          قهوة مختصة لمقهاك تبدأ بكلمة واتساب
        </motion.h2>
        <motion.p {...fadeUp} transition={{ ...fadeUp.transition, delay: 0.16 }}
          className="mt-5 text-lg text-cream/90 max-w-2xl mx-auto">
          كلّمنا الآن — نرسل لك الكتالوج الكامل، الأسعار، والعينات خلال 24 ساعة
        </motion.p>
        <motion.div {...fadeUp} transition={{ ...fadeUp.transition, delay: 0.24 }} className="mt-10 flex justify-center">
          <a
            href={getWaLink(WA_MESSAGES.final)}
            target="_blank" rel="noopener noreferrer"
            className="inline-flex items-center gap-3 rounded-full bg-gradient-gold px-10 py-5 text-lg font-black text-primary-dark shadow-gold hover:scale-[1.05] transition-transform"
          >
            <WhatsAppIcon className="h-6 w-6" />
            اطلب عرض الجملة الآن
          </a>
        </motion.div>
        <p className="mt-6 text-sm text-white/80">
          أو اتصل:{" "}
          <a href="tel:+966126526665" className="font-bold text-gold hover:underline">+966 12 652 6665</a>
          {" · "}
          <a href="tel:+966593777448" className="font-bold text-gold hover:underline">+966 59 377 7448</a>
        </p>
      </div>
    </section>
  );
}

/* ───────────────────── FOOTER ───────────────────── */
function Footer() {
  return (
    <footer className="bg-charcoal text-cream/70">
      <div className="max-w-7xl mx-auto px-6 lg:px-8 py-16 grid md:grid-cols-2 lg:grid-cols-4 gap-10">
        <div>
          <div className="text-2xl font-black text-white">Joffrey&apos;s</div>
          <p className="mt-2 text-sm text-gold">محمصة سعودية للقهوة المختصة</p>
          <p className="mt-4 text-sm leading-relaxed">
            نحمّص حبوبنا بأنفسنا في محمصتنا في المملكة العربية السعودية، ونقدم باقات جملة احترافية للمقاهي والمطاعم والفنادق.
          </p>
          <p className="mt-4 text-xs">الرقم الضريبي: 300588668200003</p>
        </div>

        <div>
          <h4 className="text-white font-bold mb-4">روابط سريعة</h4>
          <ul className="space-y-2 text-sm">
            <li><a href="#hero" className="hover:text-gold">الرئيسية</a></li>
            <li><a href="#why" className="hover:text-gold">لماذا جوفريز</a></li>
            <li><a href="#packages" className="hover:text-gold">الباقات</a></li>
            <li><a href="#faq" className="hover:text-gold">الأسئلة الشائعة</a></li>
            <li><a href="#contact" className="hover:text-gold">تواصل</a></li>
          </ul>
        </div>

        <div>
          <h4 className="text-white font-bold mb-4">تواصل معنا</h4>
          <ul className="space-y-3 text-sm">
            <li>
              <a href={getWaLink(WA_MESSAGES.floating)} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 hover:text-gold" dir="ltr">
                <WhatsAppIcon className="h-4 w-4" />
                <span>+966 59 377 7448</span>
              </a>
            </li>
            <li>
              <a href="tel:+966593777448" className="inline-flex items-center gap-2 hover:text-gold" dir="ltr">
                <Smartphone className="h-4 w-4" />
                <span>+966 59 377 7448</span>
              </a>
            </li>
            <li>
              <a href="tel:+966126526665" className="inline-flex items-center gap-2 hover:text-gold" dir="ltr">
                <Phone className="h-4 w-4" />
                <span>+966 12 652 6665</span>
              </a>
            </li>
            <li>
              <a href="mailto:Info@joffreysme.com" className="inline-flex items-center gap-2 hover:text-gold">
                <Mail className="h-4 w-4" />
                <span>Info@joffreysme.com</span>
              </a>
            </li>
            <li className="inline-flex items-center gap-2"><MapPin className="h-4 w-4" /><span>الرياض، المملكة العربية السعودية</span></li>
            <li className="inline-flex items-center gap-2"><Clock className="h-4 w-4" /><span>الأحد - الخميس 9ص - 6م</span></li>
          </ul>
        </div>

        <div>
          <h4 className="text-white font-bold mb-4">تابعنا</h4>
          <div className="flex gap-3">
            {[Instagram, Youtube].map((Icon, i) => (
              <a key={i} href="#" aria-label="social" className="h-10 w-10 inline-flex items-center justify-center rounded-full border border-cream/20 hover:border-gold hover:text-gold transition">
                <Icon className="h-4 w-4" />
              </a>
            ))}
            <a href="#" aria-label="tiktok" className="h-10 w-10 inline-flex items-center justify-center rounded-full border border-cream/20 hover:border-gold hover:text-gold transition text-xs font-black">TT</a>
            <a href="#" aria-label="x" className="h-10 w-10 inline-flex items-center justify-center rounded-full border border-cream/20 hover:border-gold hover:text-gold transition text-xs font-black">X</a>
            <a href="#" aria-label="snapchat" className="h-10 w-10 inline-flex items-center justify-center rounded-full border border-cream/20 hover:border-gold hover:text-gold transition text-xs font-black">SC</a>
          </div>
          <a href="https://joffreysme.com" target="_blank" rel="noopener noreferrer" className="mt-6 inline-block text-sm text-gold hover:underline">
            joffreysme.com →
          </a>
        </div>
      </div>

      <div className="border-t border-white/10">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-6 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs">
          <span>جميع الحقوق محفوظة © 2026 محمصة جوفريز</span>
          <span className="text-gold">Crafted by itqan.agency</span>
        </div>
      </div>
    </footer>
  );
}
